import { useEffect, useMemo, useState } from 'react';
import { useMe } from '../me';
import { isLayerShown, itemsOfShownLayers, nextLayerDefaults, type LayerChoices } from './calendar-layers';
import { CALENDAR_ITEM_KINDS, type CalendarItem, type CalendarItemKind } from './calendar-item';
import { calendarOwnerKey, readCalendarStore, writeCalendarStore } from './calendar-storage';
import { isStaffShown, type CalendarStaffEntry } from './calendar-staff-scope';
import type { CalendarData, CalendarLayer } from './use-calendar-items';

// Which layers are on — one rule for the Calendar screen and for every summary of it (a Space's card),
// so a count never includes a calendar the screen it opens leaves out. The rules are `calendar-layers.ts`.

export { isLayerShown, itemsOfShownLayers, type LayerChoices } from './calendar-layers';

const LAYER_CHOICES_STORE = 'layerChoices';
const LAYER_DEFAULTS_STORE = 'layerDefaults';
const kindChoiceId = (kind: CalendarItemKind) => `kind:${kind}`;
const staffChoiceId = (staff: string) => `staff:${staff}`;
const serviceChoiceId = (service: string) => `service:${service}`;

export function itemsOfShownKinds(items: CalendarItem[], shown: Set<CalendarItemKind>): CalendarItem[] {
  return items.filter((item) => shown.has(item.kind));
}

export function itemsOfShownServices(items: CalendarItem[], shown: Set<string>): CalendarItem[] {
  return items.filter((item) => item.kind !== 'booking' || shown.has(item.title));
}

export function useShownLayers(
  data: Pick<CalendarData, 'layers' | 'settledLayers'>,
  staff: Pick<CalendarStaffEntry, 'key'>[] = [],
  services: string[] = [],
): {
  shown: Set<string>;
  shownKinds: Set<CalendarItemKind>;
  shownStaff: Set<string>;
  shownServices: Set<string>;
  toggle: (layer: CalendarLayer) => void;
  toggleKind: (kind: CalendarItemKind) => void;
  toggleStaff: (key: string) => void;
  toggleService: (service: string) => void;
  /** Drops the reader's own choices, so every layer is back to what its provider shows. */
  reset: () => void;
  hasChoices: boolean;
} {
  const ownerKey = calendarOwnerKey(useMe());
  const [choices, setChoices] = useState<LayerChoices>({});
  const [defaults, setDefaults] = useState<LayerChoices>({});
  // Whose stored defaults are in state; until they are, nothing new is written over them.
  const [loadedFor, setLoadedFor] = useState<string | undefined>(undefined);
  useEffect(() => {
    setChoices(readCalendarStore<LayerChoices>(LAYER_CHOICES_STORE, ownerKey) ?? {});
    setDefaults(readCalendarStore<LayerChoices>(LAYER_DEFAULTS_STORE, ownerKey) ?? {});
    setLoadedFor(ownerKey);
  }, [ownerKey]);
  useEffect(() => {
    if (!ownerKey || loadedFor !== ownerKey) return;
    const next = nextLayerDefaults(data.layers, defaults, data.settledLayers);
    if (!next) return;
    setDefaults(next);
    writeCalendarStore(LAYER_DEFAULTS_STORE, ownerKey, next);
  }, [data.layers, data.settledLayers, defaults, ownerKey, loadedFor]);

  const shown = useMemo(
    () => new Set(data.layers.filter((layer) => isLayerShown(layer, choices, defaults)).map((layer) => layer.id)),
    [data.layers, choices, defaults],
  );
  const shownKinds = useMemo(
    () => new Set(CALENDAR_ITEM_KINDS.filter((kind) => choices[kindChoiceId(kind)] !== false)),
    [choices],
  );
  const shownStaff = useMemo(
    () => new Set(staff.filter((entry) => isStaffShown(entry, choices)).map((entry) => entry.key)),
    [staff, choices],
  );
  const shownServices = useMemo(
    () => new Set(services.filter((name) => choices[serviceChoiceId(name)] !== false)),
    [services, choices],
  );
  const toggle = (layer: CalendarLayer) => {
    setChoices((current) => {
      const next = { ...current, [layer.id]: !shown.has(layer.id) };
      writeCalendarStore(LAYER_CHOICES_STORE, ownerKey, next);
      return next;
    });
  };
  const toggleKind = (kind: CalendarItemKind) => {
    setChoices((current) => {
      const id = kindChoiceId(kind);
      const next = { ...current, [id]: !shownKinds.has(kind) };
      writeCalendarStore(LAYER_CHOICES_STORE, ownerKey, next);
      return next;
    });
  };
  const toggleStaff = (key: string) => {
    setChoices((current) => {
      const id = staffChoiceId(key);
      const next = { ...current, [id]: !shownStaff.has(key) };
      writeCalendarStore(LAYER_CHOICES_STORE, ownerKey, next);
      return next;
    });
  };
  const toggleService = (name: string) => {
    setChoices((current) => {
      const id = serviceChoiceId(name);
      const next = { ...current, [id]: !shownServices.has(name) };
      writeCalendarStore(LAYER_CHOICES_STORE, ownerKey, next);
      return next;
    });
  };
  const reset = () => {
    setChoices({});
    writeCalendarStore(LAYER_CHOICES_STORE, ownerKey, {});
  };
  const hasChoices =
    data.layers.some((layer) => choices[layer.id] !== undefined)
    || CALENDAR_ITEM_KINDS.some((kind) => choices[kindChoiceId(kind)] !== undefined)
    || staff.some((entry) => choices[staffChoiceId(entry.key)] !== undefined)
    || services.some((name) => choices[serviceChoiceId(name)] !== undefined);
  return { shown, shownKinds, shownStaff, shownServices, toggle, toggleKind, toggleStaff, toggleService, reset, hasChoices };
}
